import { useCallback, useReducer } from 'react';

import clsx from 'clsx';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { ReactComponent as Stops } from '@quick/assets/icons/form/stops.svg';
import { Button } from '@quick/components/Button';

import { useAppTranslation } from '@/app/i18n/client';
import { ReactComponent as Plus2 } from '@/assets/plus2.svg';
import { useCountry } from '@/layers/core/deal/services/CountryContext';
import { idToCountry } from '@/layers/core/domain/countryMap';

import {
  animationClassName,
  focusWithinClassName,
  gapClassName,
  groupClassName,
} from '../classNames';
import { useFormDataContext } from '../FormDataContext';
import { CommonTitle } from '../Titles';
import type { IForm } from '../types';
import { getNewAddress } from '../utils';

import { Address } from './Address';
import { RoundTrip } from './RoundTrip';
import { ViewAddressesModal } from './ViewAddressesModal';

export const Addresses = ({ modal }: { modal?: boolean }) => {
  const { t } = useAppTranslation(['common', 'demand']);
  // Register is used to force an error. See below
  const { control, register, trigger } = useFormContext<IForm>();
  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'stops',
  });
  const { modality, serviceType, user, line, country_id } =
    useFormDataContext();

  const { country: countryAbbr } = useCountry();
  const [showAddressModal, toggleShowAddressModal] = useReducer(
    (b: boolean) => !b,
    false
  );

  const addAddress = useCallback(() => {
    append(getNewAddress());
  }, [append]);
  if (!modality) return null;
  // Show error if service type uses stop type and does not have a stop type configured
  if (
    (serviceType?.full_stop_description || serviceType?.comment_by_selection) &&
    !serviceType?.type_stops.length
  )
    return (
      <p
        {...register('error' as 'stops', {
          required: true,
          shouldUnregister: true,
        })}
        tabIndex={0}
        className="text-center text-sm font-semibold text-red-500 focus:text-base focus:font-bold
          focus:underline sm:col-span-2 dark:text-red-300"
      >
        {t('demand:addresses.noType')}
      </p>
    );

  const country = idToCountry?.[user?.country_id as 1];

  const isCleanCO = country_id === 1 && line?.line_filter === 'clean';
  return (
    <div
      className={clsx(
        // use z-20 to avoid react select options to be hidden by other input labels
        'z-20 flex flex-col',
        groupClassName,
        gapClassName,
        animationClassName,
        focusWithinClassName
      )}
    >
      <div className="flex flex-wrap gap-4">
        <CommonTitle
          title={t(isCleanCO ? 'common:address' : 'common:stops')}
          Icon={Stops}
        />
        <div className="ml-auto flex flex-wrap justify-end gap-4">
          {!modality.single_address && <RoundTrip />}
          <Button
            variant="reject"
            kind="secondary"
            onClick={async () => {
              if (await trigger('stops')) toggleShowAddressModal();
            }}
          >
            {t('demand:addresses.map')}
          </Button>
          {!modality.single_address && (
            <Button
              onClick={addAddress}
              kind="secondary"
              variant="accept"
              className="flex items-center gap-4"
            >
              <Plus2 className="w-4" />
              {t('common:add')}
            </Button>
          )}
        </div>
      </div>
      {fields?.map(({ uuid, ...address }, index) => (
        <Address
          key={address.id} // important to include the key so fields don't break
          uuid={uuid}
          index={index}
          address={{ ...address, uuid }}
          modality={modality}
          modal={modal}
          serviceType={serviceType}
          removeAddress={fields.length > 2 ? () => remove(index) : undefined}
          updateAddress={update}
        />
      ))}
      {showAddressModal && (
        <ViewAddressesModal
          onClose={toggleShowAddressModal}
          country={country || countryAbbr}
          useFirstAddress={serviceType?.use_first_address}
        />
      )}
    </div>
  );
};
